import * as React from "react";
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useApiClient } from "../../../api";
import { useNavigate } from "react-router-dom";

export const CreateStudentDialog = ({
  open,
  setOpen,
  schools = [],
  yearGroups = [],
  schoolClass = null,
  skipRedirect = false,
  onSuccess = () => {},
}) => {
  const ApiClient = useApiClient();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [yearGroup, setYearGroup] = useState("");
  const [school, setSchool] = useState(schools[0]);
  const [identifier, setIdentifier] = useState("");
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (schools.length > 0) {
      setSchool(schools[0]);
    }
  }, [schools]);

  const handleSubmit = async () => {
    setError(null);

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      // TODO: email is still optional here?
      setError("Incorrect or missing email");
    }
    try {
      const response = await ApiClient.schools.createStudent({
        studentIdentifier: identifier,
        firstName: firstName,
        lastName: lastName,
        yearGroup: yearGroup?.id,
        school: school?.id,
        email: email,
        schoolClass: schoolClass?.id,
      });
      if (response.success) {
        setOpen(false);
        if (skipRedirect) {
          onSuccess();
        } else {
          navigate(`view/${response.student.id}`);
        }
      } else {
        setError(response.message || "Failed to create");
      }
    } catch (error) {
      console.error("Failed to create a student", error);
      setError("An error occurred while creating the student.");
    }
  };

  const handleClose = () => {
    setError(null);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: {
          component: "form",
          sx: { backgroundImage: "none" },
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography variant={"h4"}>Add a new Student</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {!schoolClass && (
            <TextField
              label="Student Identifier"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              fullWidth
            />
          )}
          <TextField
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
          />
          {!schoolClass && (
            <>
              {schools.length !== 1 && (
                <FormControl>
                  <InputLabel id="school-label">School</InputLabel>
                  <Select
                    labelId="school-label"
                    value={school}
                    onChange={(e) => {
                      setSchool(e.target.value);
                    }}
                    fullWidth
                  >
                    {schools.map((school) => (
                      <MenuItem key={school.id} value={school}>
                        {school.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl>
                <InputLabel id="year-group-label">Year Group</InputLabel>
                <Select
                  labelId={"year-group-label"}
                  label="Year Group"
                  value={yearGroup}
                  onChange={(e) => setYearGroup(e.target.value)}
                  fullWidth
                >
                  {yearGroups.map((year) => (
                    <MenuItem key={year.id} value={year}>
                      Year {year.yearGroup}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <TextField
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  autoFocus
                  required
                  fullWidth
                  variant="outlined"
                />
              </FormControl>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant={"outlined"}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant={"contained"}
          disabled={!firstName}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
